import type {
  ImportQuestionRequest,
  ImportRuleRequest,
  ImportSectionRequest,
  ImportStudyRequest,
} from "@/types/requests";
import type { DemographicSetting } from "@/types/resources";
import { RuleOperator } from "@/types/utils";

import {
  NATIONAL_FEEDS,
  NATIONAL_NEWS,
  STATE_BRIEFINGS,
  STATE_FEEDS,
} from "./briefings";
import type {
  Candidate,
  ExpectCode,
  NewsFeed,
  Office,
  Race,
  RaceOffice,
  RecallCode,
  StateInfo,
  VoteCode,
} from "./types";

/* ── Questionnaire design ───────────────────────────
 * One study per state, fielded to that state's adults (18+). Before the
 * first question the interviewer reads a neutral briefing (the model's
 * knowledge ends in June 2026): the date, how the state votes, who is on the
 * ballot and the news since June, with no polls or forecasts. By default only
 * what feeds the forecast is asked. Order matters: vote choice is asked first
 * (unprimed), then the "neighbor" read, then (optionally) expectations and
 * context (approval, issues, finances), and 2024 recall last so it doesn't
 * anchor the 2026 answers.
 *
 * Three independent reads of each race:
 *   1. Own vote ("who would you vote for") — classic intention, weighted by
 *      likelihood to vote and calibrated on recalled 2024 vote.
 *   2. Neighbors ("how will the people around you vote") — the social-circle
 *      question (Galesic et al., 2018; the 2024 "neighbor effect" polls). It
 *      sidesteps social-desirability bias and pools what respondents observe.
 *      Its own bias is measured with the same question about 2024.
 *   3. Expectations ("who will win") — the wisdom-of-crowds read
 *      (Rothschild & Wolfers), shown alongside but not blended. Senate and
 *      governor races only: the House generic ballot names no candidates.
 */

export const TRACKER_PREFIX = "Midterms 2026";
export const QUESTIONNAIRE_VERSION = 3;

/* Partisan-feed study: fielded next to a state's tracker study. Respondents
 * give their 2024 vote first, then hear the stories their side's outlets
 * ran: Trump voters right-leaning, Harris voters left-leaning, everyone else
 * the wires. The tracker files and relaunches it but keeps it off the map. */
export const FEEDS_LABEL = "Partisan news";

const NAME_PATTERN = new RegExp(
  `^${TRACKER_PREFIX} · ([A-Z]{2}) · Wave (\\d+)( · ${FEEDS_LABEL})?$`
);

/* Feed sections in questionnaire order. Respondents whose 2024 vote routes
 * nowhere fall through to the first one. */
const FEED_ORDER: NewsFeed[] = ["wire", "right", "left"];
const RECALL_FEED: Partial<Record<RecallCode, NewsFeed>> = {
  H: "left",
  T: "right",
};

/** Turnout options with their likely-voter weight. */
export const TURNOUT: { label: string; weight: number }[] = [
  { label: "I have already voted, early or by mail", weight: 1 },
  { label: "I will definitely vote", weight: 0.9 },
  { label: "I will probably vote", weight: 0.65 },
  { label: "Chances are about 50-50", weight: 0.35 },
  { label: "I will probably not vote", weight: 0.1 },
  { label: "I will definitely not vote", weight: 0 },
  { label: "I am not registered or not eligible to vote", weight: 0 },
];

const RECALL: { code: RecallCode; label: string }[] = [
  { code: "T", label: "Donald Trump, the Republican" },
  { code: "H", label: "Kamala Harris, the Democrat" },
  { code: "O", label: "Someone else" },
  { code: "N", label: "I did not vote" },
  { code: "X", label: "I was not eligible to vote in 2024" },
];

export const APPROVAL = [
  "Strongly approve",
  "Somewhat approve",
  "Somewhat disapprove",
  "Strongly disapprove",
  "Not sure",
];

export const ISSUES = [
  "Cost of living and inflation",
  "Jobs and the economy",
  "Immigration and the border",
  "Health care",
  "Abortion",
  "Democracy and the rule of law",
  "Crime and public safety",
  "Taxes and government spending",
  "Tariffs and trade",
  "Something else",
];

export const FINANCES = ["Better off", "About the same", "Worse off"];

/** Left-side share implied by each neighbor bucket (index 0 = nearly all left). */
export const NEIGHBOR_SHARES = [0.93, 0.8, 0.62, 0.5, 0.38, 0.2, 0.07];

const VOTE_TAIL: { code: VoteCode; label: string }[] = [
  { code: "O", label: "Another candidate" },
  { code: "N", label: "I would not vote in this race" },
  { code: "U", label: "Undecided" },
];

const EXPECT_UNSURE = "Too close to call / not sure";

const PARTY_NOUN: Record<Candidate["party"], string> = {
  D: "Democrat",
  I: "Independent",
  R: "Republican",
};

/* ── Question identification ────────────────────── */

export type QuestionKey =
  | "approval"
  | "finances"
  | "issue"
  | "neighbors2024"
  | "recall"
  | "turnout"
  | "why"
  | `${Office}.${"expect" | "neighbors" | "vote"}`;

const OFFICE_PATTERNS: Record<Office, RegExp> = {
  governor: /Governor/,
  house: /U\.S\. House/,
  senate: /U\.S\. Senate/,
};

/** Maps a fielded question text back to its role in the questionnaire. */
export const identifyQuestion = (text: string): QuestionKey | null => {
  const t = text.trim();
  if (t.startsWith("The general election is on")) return "turnout";
  if (t.startsWith("Do you approve or disapprove")) return "approval";
  if (t.startsWith("Which one of these issues")) return "issue";
  if (t.startsWith("Compared with a year ago")) return "finances";
  if (t.startsWith("In the 2024 presidential election")) return "recall";
  if (t.startsWith("Thinking back to the 2024 presidential election")) {
    return "neighbors2024";
  }
  if (t.startsWith("In a sentence or two")) return "why";

  const stem = t.startsWith("If the November 3, 2026 election")
    ? "vote"
    : t.startsWith("Think about the people who live around you")
      ? "neighbors"
      : t.startsWith("Regardless of how you plan to vote")
        ? "expect"
        : null;
  if (!stem) return null;
  for (const office of ["senate", "governor", "house"] as const) {
    if (OFFICE_PATTERNS[office].test(t)) return `${office}.${stem}`;
  }
  return null;
};

/**
 * Vote code of each option of a fielded vote question: left, right, then
 * any named candidates, then "Another candidate" / "would not vote" /
 * "Undecided". Extra Republicans (Alaska's top-four ballot) count with the
 * Republican side, extra Democrats with a Democratic left side.
 */
export const voteCodes = (options: string[]): VoteCode[] =>
  options.map((label, i) => {
    if (i === 0) return "L";
    if (i === 1) return "R";
    const fromEnd = options.length - i;
    if (fromEnd === 1) return "U";
    if (fromEnd === 2) return "N";
    if (fromEnd === 3) return "O";
    if (/\(Republican\)$/.test(label)) return "R";
    if (/\(Democrat\)$/.test(label) && /\(Democrat\)$/.test(options[0])) {
      return "L";
    }
    return "O";
  });

export const decodeExpect = (choice: number): ExpectCode | null =>
  choice === 0 ? "L" : choice === 1 ? "R" : choice === 2 ? "U" : null;

export const decodeRecall = (choice: number): RecallCode | null =>
  RECALL[choice]?.code ?? null;

/** Parses a tracker study name into its state, wave and kind. */
export const parseStudyName = (
  name: string
): { feeds: boolean; state: string; wave: number } | null => {
  const match = NAME_PATTERN.exec(name.trim());
  return match
    ? { feeds: Boolean(match[3]), state: match[1], wave: Number(match[2]) }
    : null;
};

export const studyName = (state: string, wave: number, feeds = false): string =>
  `${TRACKER_PREFIX} · ${state} · Wave ${wave}${feeds ? ` · ${FEEDS_LABEL}` : ""}`;

/* ── Briefing ───────────────────────────────────── */

const BRIEFING_RACE_LABEL: Record<RaceOffice, string> = {
  governor: "Governor",
  senate: "U.S. Senate",
};

/** What the interviewer reads before the first question. */
export const briefingText = (state: StateInfo, date: Date): string => {
  const briefing = STATE_BRIEFINGS[state.abbr];
  const day = date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    weekday: "long",
    year: "numeric",
  });
  const races = (["senate", "governor"] as const).flatMap((office) => {
    const text = briefing?.races[office];
    return text ? [`- ${BRIEFING_RACE_LABEL[office]}: ${text}`] : [];
  });
  const news = [...NATIONAL_NEWS, ...(briefing?.news ?? [])];

  return [
    `Before we begin, here is a short summary of where things stand in the election as of today, ${day}.`,
    ...(briefing
      ? ["", `How voting works in ${state.name}: ${briefing.voting}`]
      : []),
    "",
    "On the ballot on Tuesday, November 3:",
    ...races,
    "- U.S. House: every seat is up for election, including your district's.",
    "",
    "In the news since June:",
    ...news.map((item) => `- ${item}`),
  ].join("\n");
};

/**
 * What the interviewer reads at the start of a partisan-feed section: the
 * stories one side's outlets ran, national first, then the state's races.
 */
export const feedText = (state: StateInfo, feed: NewsFeed): string => {
  // A national story can also be one of the state's (Alaska's Senate race).
  const stories = [
    ...NATIONAL_FEEDS[feed],
    ...(STATE_FEEDS[state.abbr]?.[feed] ?? []),
  ].filter(
    (story, i, all) => all.findIndex((s) => s.headline === story.headline) === i
  );
  return [
    "Here are some stories you have come across lately in the news you follow:",
    ...stories.map((s) => `- ${s.outlet}: "${s.headline}" — ${s.summary}`),
  ].join("\n");
};

/* ── Question builders ──────────────────────────── */

/** How a candidate is referred to: "Ossoff", "Huckabee Sanders". */
export const shortName = (candidate: Candidate): string => {
  if (candidate.short) return candidate.short;
  const parts = candidate.name
    .replace(/,? (Jr\.|Sr\.|II|III|IV)$/, "")
    .split(" ");
  return parts[parts.length - 1];
};

const sideLabel = (candidate: Candidate | null, fallback: string): string =>
  candidate
    ? `${candidate.name} (${candidate.affiliation ?? PARTY_NOUN[candidate.party]})`
    : fallback;

const sideShort = (candidate: Candidate | null, fallback: string): string =>
  candidate ? shortName(candidate) : fallback;

const question = (
  text: string,
  options: string[],
  type: ImportQuestionRequest["type"] = "single_choice"
): ImportQuestionRequest => ({
  assignment: null,
  carry_forward: null,
  cells: [],
  config: null,
  lines: [],
  options,
  rules: [],
  section_order: null,
  text,
  type,
});

const neighborOptions = (left: string, right: string): string[] => [
  `Nearly all for ${left} (90% or more)`,
  `Most for ${left} (about 70-90%)`,
  `Somewhat more for ${left} (about 55-70%)`,
  "About evenly split",
  `Somewhat more for ${right} (about 55-70%)`,
  `Most for ${right} (about 70-90%)`,
  `Nearly all for ${right} (90% or more)`,
];

const RACE_TITLE: Record<RaceOffice, (state: StateInfo) => string> = {
  governor: (state) => `Governor of ${state.name}`,
  senate: (state) => `U.S. Senate in ${state.name}`,
};

/** Text of the vote question fielded for an office in a state. */
export const voteQuestion = (office: Office, state: StateInfo): string =>
  office === "house"
    ? "If the November 3, 2026 election for the U.S. House of Representatives in your district were held today, would you vote for the Democratic candidate or the Republican candidate?"
    : `If the November 3, 2026 election for ${RACE_TITLE[office](state)} were held today, who would you vote for?`;

const raceQuestions = (
  race: Race,
  state: StateInfo,
  withExpectation: boolean
): ImportQuestionRequest[] => {
  const title = RACE_TITLE[race.office](state);
  const leftFallback =
    race.left?.party === "I"
      ? "the independent candidate"
      : "the Democratic candidate";
  const left = sideLabel(race.left, leftFallback);
  const right = sideLabel(race.right, "the Republican candidate");
  const others = race.others.map((c) => sideLabel(c, c.name));

  return [
    question(voteQuestion(race.office, state), [
      left,
      right,
      ...others,
      ...VOTE_TAIL.map((o) => o.label),
    ]),
    question(
      `Think about the people who live around you: your neighbors and others in your community. How do you think they will vote in the race for ${title}?`,
      neighborOptions(
        sideShort(race.left, leftFallback),
        sideShort(race.right, "the Republican")
      )
    ),
    ...(withExpectation
      ? [
          question(
            `Regardless of how you plan to vote, who do you think will win the race for ${title}?`,
            [left, right, EXPECT_UNSURE]
          ),
        ]
      : []),
  ];
};

const houseQuestions = (state: StateInfo): ImportQuestionRequest[] => [
  question(voteQuestion("house", state), [
    "The Democratic candidate",
    "The Republican candidate",
    ...VOTE_TAIL.map((o) => o.label),
  ]),
  question(
    "Think about the people who live around you: your neighbors and others in your community. How do you think they will vote for the U.S. House of Representatives?",
    neighborOptions("the Democrat", "the Republican")
  ),
];

/* ── Study payload ──────────────────────────────── */

const RECALL_TEXT = "In the 2024 presidential election, who did you vote for?";

const FOLLOWING = question(
  "How closely have you been following news about this year's elections?",
  ["Very closely", "Somewhat closely", "Not too closely", "Not at all closely"]
);

export interface WaveOptions {
  /** Fielding date, as told to respondents in the briefing. */
  date: Date;
  /** The state's partisan-feed study rather than its tracker study. */
  feeds: boolean;
  /** Also ask who will win, Trump approval, top issue and finances. None of
   *  them moves the forecast. */
  includeContext: boolean;
  includeWhy: boolean;
  offices: Office[];
  size: ImportStudyRequest["groups"][number]["size"];
  wave: number;
}

/** Questions fielded in one state for the selected offices. */
export const stateQuestions = (
  state: StateInfo,
  races: Race[],
  options: Pick<WaveOptions, "includeContext" | "includeWhy" | "offices">
): ImportQuestionRequest[] => {
  const raceBlocks = (["senate", "governor"] as const)
    .filter((office) => options.offices.includes(office))
    .flatMap((office) =>
      races
        .filter((r) => r.office === office && r.state === state.abbr)
        .flatMap((r) => raceQuestions(r, state, options.includeContext))
    );

  return [
    question(
      "The general election is on Tuesday, November 3, 2026. Which of these best describes you?",
      TURNOUT.map((o) => o.label)
    ),
    ...raceBlocks,
    ...(options.offices.includes("house") ? houseQuestions(state) : []),
    ...(options.includeContext
      ? [
          question(
            "Do you approve or disapprove of the way Donald Trump is handling his job as president?",
            APPROVAL
          ),
          question(
            "Which one of these issues will matter most to your vote this November?",
            ISSUES
          ),
          question(
            "Compared with a year ago, is your household financially better off, worse off, or about the same?",
            FINANCES
          ),
        ]
      : []),
    question(
      RECALL_TEXT,
      RECALL.map((o) => o.label)
    ),
    question(
      "Thinking back to the 2024 presidential election, how do you think the people who live around you voted?",
      neighborOptions("Harris", "Trump")
    ),
    ...(options.includeWhy
      ? [
          question(
            "In a sentence or two, what is the main reason behind how you will vote (or not vote) this November?",
            [],
            "open_ended"
          ),
        ]
      : []),
  ];
};

/** Offices with a race to ask about in a state (the House is everywhere). */
export const officesInState = (
  state: string,
  races: Race[],
  selected: Office[]
): Office[] =>
  selected.filter(
    (office) =>
      office === "house" ||
      races.some((r) => r.office === office && r.state === state)
  );

const groupDemographics = (state: StateInfo): DemographicSetting[] => [
  {
    demographic: "country",
    distribution: [
      {
        label: "United States",
        states: [{ label: state.name, value: state.fips, weight: 1 }],
        value: "us",
        weight: 1,
      },
    ],
    label: "Country",
  },
  {
    demographic: "age",
    distribution: [
      { label: "18-100", value: { max: 100, min: 18 }, weight: 1 },
    ],
    label: "Age",
  },
];

/**
 * Every question of a state's study, attached to its section. The
 * partisan-feed study asks the 2024 vote first, routes each respondent
 * through their side's feed section, then joins the same questionnaire.
 */
export const studyQuestions = (
  state: StateInfo,
  races: Race[],
  options: Pick<
    WaveOptions,
    "feeds" | "includeContext" | "includeWhy" | "offices"
  >
): ImportQuestionRequest[] => {
  const questions = stateQuestions(state, races, options);
  if (!options.feeds) return questions.map((q) => ({ ...q, section_order: 1 }));

  // 1-based: the 2024 vote, one question per feed section, then the
  // questionnaire, whose section has the same number as its first question.
  const main = 2 + FEED_ORDER.length;
  const jump = (target: number, option: number | null): ImportRuleRequest => ({
    operator: RuleOperator.Equal,
    option_index: option,
    probability: null,
    source_question_order: null,
    target_question_order: target,
  });
  return [
    {
      ...question(
        RECALL_TEXT,
        RECALL.map((o) => o.label)
      ),
      rules: RECALL.flatMap((o, i) => {
        const feed = RECALL_FEED[o.code];
        return feed ? [jump(2 + FEED_ORDER.indexOf(feed), i)] : [];
      }),
      section_order: 1,
    },
    ...FEED_ORDER.map((_, i) => ({
      ...FOLLOWING,
      // The last feed section runs straight into the questionnaire.
      rules: i < FEED_ORDER.length - 1 ? [jump(main, null)] : [],
      section_order: 2 + i,
    })),
    ...questions
      .filter((q) => q.text !== RECALL_TEXT)
      .map((q) => ({ ...q, section_order: main })),
  ];
};

/** A study's sections; each description is read as respondents reach it. */
const studySections = (
  state: StateInfo,
  options: Pick<WaveOptions, "date" | "feeds">
): ImportSectionRequest[] => {
  const section = (
    title: string,
    description: string | null
  ): ImportSectionRequest => ({
    cells: [],
    config: null,
    description,
    title,
    use_case: null,
  });
  const briefing = section(
    "Election briefing",
    briefingText(state, options.date)
  );
  return options.feeds
    ? [
        briefing,
        ...FEED_ORDER.map((feed) =>
          section("News you follow", feedText(state, feed))
        ),
        section("Questionnaire", null),
      ]
    : [briefing];
};

export const buildStudyRequest = (
  state: StateInfo,
  races: Race[],
  options: WaveOptions
): ImportStudyRequest => ({
  description: options.feeds
    ? `Foresight midterm tracker · ${state.name} · partisan-feed study · questionnaire v${QUESTIONNAIRE_VERSION}. Created from the admin Midterms tab; not shown on the map.`
    : `Foresight midterm tracker · ${state.name} · questionnaire v${QUESTIONNAIRE_VERSION}. Created from the admin Midterms tab; do not edit the questions.`,
  folder_id: null,
  groups: [
    {
      demographics: groupDemographics(state),
      description: `Adults (18+) living in ${state.name}, drawn from Foresight's census-grounded US population.`,
      name: `${state.name} adults`,
      panel_id: null,
      size: options.size,
    },
  ],
  name: studyName(state.abbr, options.wave, options.feeds),
  // Section descriptions reach respondents as interviewer messages and stay
  // in their conversation for the questions that follow.
  questions: studyQuestions(state, races, options),
  sections: studySections(state, options),
  type: "quantitative",
  use_case: null,
});

/** Credits held for one study (mirrors RunsManager.estimate_credits). */
export const estimateCredits = (
  questions: ImportQuestionRequest[],
  size: number
): number =>
  questions.reduce((sum, q) => sum + (q.type === "open_ended" ? 2 : 1), 0) *
  size;
